/**
 * Section "Propositions ESS BATICO" (devis générés par le simulateur).
 * Extrait de DocumentSections.js (refactor P2 — Feb 2026).
 * Aperçu pro via GEDDocumentViewerModal (Vague 21 — Feb 2026).
 */
import React, { useState, useEffect, useCallback } from 'react';
import { EcheancierCarte } from './EcheancierCarte';
import { MandatSepaLigne } from './MandatSepaLigne';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import {
  Sparkles, Eye, Download, ArrowRight, Loader2, Trash2, CheckCircle,
  AlertTriangle, Clock, FileText, Calendar, Mail, Calculator, History, ChevronDown, RefreshCw,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { downloadDocument } from '../../services/downloadService';
import { trackEvent } from '../../lib/analytics';
import { formatEUR, extractMontantTTC, extractValidite } from './helpers';
import GEDDocumentViewerModal from '../GED/GEDDocumentViewerModal';
import { useDocumentManager } from '../../hooks/useDocumentManager';
import { getToken } from '../../services/tokenService';
import { PropositionSignatureAction } from './PropositionSignatureAction';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Construit l'URL du PDF à prévisualiser pour une proposition ESS.
 * Gère 4 cas (par ordre de priorité) :
 *  1. devis généré par le simulateur (`download-devis/<devis_id>`)
 *  2. RCDS associé (souscription_id)
 *  3. URL fournie directement par le doc
 *  4. Legacy GED par path
 */
function resolvePropositionPreviewUrl(doc) {
  const token = getToken();
  const devisId = (doc.metadata && doc.metadata.devis_id) || doc.reference;
  const directUrl = doc.url || doc.download_url || '';

  // 1. Devis simulateur — URL avec mode=view
  if (directUrl.includes('/simulateur/download-devis/')
      || doc.type === 'devis_ess_genere'
      || (doc.source === 'ged_simulateur' && devisId)) {
    const path = directUrl.includes('/simulateur/download-devis/')
      ? directUrl
      : `/api/souscriptions/simulateur/download-devis/${encodeURIComponent(devisId)}`;
    const cleanPath = path.startsWith('http') ? path : `${API}${path.startsWith('/') ? '' : '/'}${path}`;
    return `${cleanPath}${cleanPath.includes('?') ? '&' : '?'}mode=view&token=${encodeURIComponent(token || '')}`;
  }
  // 2. Souscription RCDS
  if (doc.souscription_id) {
    const docType = doc.type === 'devis_ess' ? 'devis' : 'pack1';
    return `${API}/api/souscriptions/${encodeURIComponent(doc.souscription_id)}/download-${docType}?mode=view&token=${encodeURIComponent(token || '')}`;
  }
  // 3. URL directe
  if (directUrl) {
    const path = directUrl.startsWith('http') ? directUrl : `${API}${directUrl.startsWith('/') ? '' : '/'}${directUrl}`;
    return `${path}${path.includes('?') ? '&' : '?'}mode=view&token=${encodeURIComponent(token || '')}`;
  }
  // 4. Legacy GED
  if (doc.id) {
    return `${API}/api/ged/documents/${encodeURIComponent(doc.id)}/download?mode=view&token=${encodeURIComponent(token || '')}`;
  }
  return null;
}

export default function PropositionsESSSection({ items, membreId, selection, onToggle, onDeleteOne }) {
  const navigate = useNavigate();
  const [loadingId, setLoadingId] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);
  const { previewDocument: previewDocumentBlob } = useDocumentManager(getToken());
  const isSelected = (id) => selection?.has?.(`proposition_ess:${id}`);

  // Historique des versions par devis_id de référence (cache local).
  // Format : { [devis_id_courant]: [{ devis_id, version, montant_ttc, montant_ht, ca_ht, type_souscription, created_at }] }
  const [versionsByDoc, setVersionsByDoc] = useState({});
  const [versionsLoading, setVersionsLoading] = useState({});

  // Statut FTPE du membre — déterminant pour le « Total indicatif du parcours
  // coopératif » : on ajoute 200€ (cotisation FTPE annuelle) si le membre
  // n'est PAS encore adhérent FTPE. Cette logique est strictement identique
  // à celle appliquée dans le simulateur (CalculateurBATICO/ResultatsSection).
  // Lecture via l'endpoint dédié, fallback false en cas d'erreur.
  const [isFtpeMember, setIsFtpeMember] = useState(false);
  useEffect(() => {
    if (!membreId) return;
    const token = getToken();
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(
          `${API}/api/ftpe-adhesion/member/${encodeURIComponent(membreId)}/ftpe-status`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!cancelled) {
          setIsFtpeMember(res.data?.has_ftpe === true && res.data?.status === 'adherent');
        }
      } catch {
        if (!cancelled) setIsFtpeMember(false);
      }
    })();
    return () => { cancelled = true; };
  }, [membreId]);

  // Statut FTPE propre à chaque devis (déclaré « déjà adhérent » dans le calculateur)
  const [dejaAdherentByDoc, setDejaAdherentByDoc] = useState({});
  useEffect(() => {
    if (!items?.length) return;
    const token = getToken();
    if (!token) return;
    const refs = [...new Set(items.map((d) => (d.metadata && d.metadata.devis_id) || d.reference).filter(Boolean))];
    refs.forEach((ref) => {
      axios
        .get(`${API}/api/souscriptions/simulateur/ftpe-statut/${encodeURIComponent(ref)}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((res) => setDejaAdherentByDoc((s) => ({ ...s, [ref]: res.data?.deja_adherent === true })))
        .catch(() => {});
    });
  }, [items]);

  // Charge l'historique des versions pour les propositions ayant potentiellement
  // une lignée. On appelle l'endpoint pour chaque devis distinct (le backend gère
  // les legacy sans parent → retourne juste v1).
  useEffect(() => {
    if (!items?.length) return;
    const token = getToken();
    if (!token) return;
    const seen = new Set();
    items.forEach((doc) => {
      const ref = (doc.metadata && doc.metadata.devis_id) || doc.reference;
      if (!ref || seen.has(ref)) return;
      seen.add(ref);
      setVersionsLoading((s) => ({ ...s, [ref]: true }));
      axios
        .get(`${API}/api/batico/proposition/by-parent/${encodeURIComponent(ref)}/versions`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((res) => {
          setVersionsByDoc((s) => ({ ...s, [ref]: res.data || [] }));
        })
        .catch(() => {
          setVersionsByDoc((s) => ({ ...s, [ref]: [] }));
        })
        .finally(() => {
          setVersionsLoading((s) => ({ ...s, [ref]: false }));
        });
    });
  }, [items]);

  useEffect(() => {
    if (!items?.length) return;
    items.forEach((doc) => {
      const ref = (doc.metadata && doc.metadata.devis_id) || doc.reference || doc.id;
      trackEvent(
        'proposition_card_viewed',
        { reference: ref, doc_id: doc.id, source: doc.source || 'unknown', membre_id: membreId },
        { dedupKey: `${ref}` },
      );
    });
  }, [items, membreId]);

  if (!items?.length) return null;

  const handleSouscrire = (doc) => {
    const devisId = (doc.metadata && doc.metadata.devis_id) || doc.reference || doc.id || '';
    trackEvent('proposition_card_souscrire_clicked', { reference: devisId, doc_id: doc.id, membre_id: membreId });
    let role = '';
    try {
      role = JSON.parse(localStorage.getItem('user') || '{}').role || '';
    } catch { /* noop */ }
    const target = (role === 'beneficiaire_pro' || role === 'member')
      ? '/member/souscription'
      : '/cooper/souscription-annuelle';
    navigate(`${target}?proposition=${encodeURIComponent(devisId)}&from=membres&membre=${encodeURIComponent(membreId || '')}`);
  };

  const handleRelancer = (doc) => {
    const devisId = (doc.metadata && doc.metadata.devis_id) || doc.reference || doc.id || '';
    const params = new URLSearchParams({ relance: devisId });
    if (membreId) params.set('membre_id', membreId);
    navigate(`/calculateur-batico?${params.toString()}`);
  };

  const handleReprendreSimulateur = (doc) => {
    const devisId = (doc.metadata && doc.metadata.devis_id) || doc.reference || doc.id || '';
    trackEvent('proposition_card_reprendre_simulateur_clicked', {
      reference: devisId, doc_id: doc.id, membre_id: membreId,
    });
    const params = new URLSearchParams({
      proposition: devisId,
      from: 'batico_resume',
    });
    if (membreId) params.set('membre', membreId);
    navigate(`/calculateur-batico?${params.toString()}`);
  };

  // Construit un faux "doc" pour cibler une version spécifique de la lignée
  // (autre que la version affichée par la carte). On reprend les métadonnées
  // du doc parent et on remplace juste le devis_id pour que les helpers de
  // résolution d'URL (`resolvePropositionPreviewUrl`, `downloadDocument`)
  // ciblent la bonne version.
  const buildVersionDoc = useCallback((parentDoc, version) => {
    const versionedRef = version.devis_id;
    return {
      ...parentDoc,
      id: `${parentDoc.id}::v${version.version}`,  // id unique côté UI (cache, preview)
      reference: versionedRef,
      url: '',
      download_url: '',
      nom: `${parentDoc.nom || 'Proposition ESS BATICO'} — v${version.version}`,
      metadata: {
        ...parentDoc.metadata,
        devis_id: versionedRef,
        version: version.version,
        is_version_pick: true,
      },
    };
  }, []);

  const handleVersionPreview = (parentDoc, version) => {
    const versionedDoc = buildVersionDoc(parentDoc, version);
    trackEvent('proposition_version_previewed', {
      reference: version.devis_id, version: version.version, membre_id: membreId,
    });
    setPreviewDoc({
      id: versionedDoc.id,
      original_filename: versionedDoc.nom + '.pdf',
      filename: versionedDoc.nom + '.pdf',
      mime_type: 'application/pdf',
      source: 'PROPOSITION_ESS',
      _previewUrl: resolvePropositionPreviewUrl(versionedDoc),
      _originalDoc: versionedDoc,
    });
  };

  const handleVersionDownload = async (parentDoc, version) => {
    const versionedDoc = buildVersionDoc(parentDoc, version);
    trackEvent('proposition_version_downloaded', {
      reference: version.devis_id, version: version.version, membre_id: membreId,
    });
    setLoadingId(`${parentDoc.id}::v${version.version}`);
    try { await downloadDocument(versionedDoc); }
    finally { setLoadingId(null); }
  };

  const handleVersionResume = (version) => {
    trackEvent('proposition_version_resume_clicked', {
      reference: version.devis_id, version: version.version, membre_id: membreId,
    });
    const params = new URLSearchParams({
      proposition: version.devis_id,
      from: 'batico_resume',
    });
    if (membreId) params.set('membre', membreId);
    navigate(`/calculateur-batico?${params.toString()}`);
  };

  const handleDownload = async (doc) => {
    const ref = (doc.metadata && doc.metadata.devis_id) || doc.reference || doc.id;
    trackEvent('proposition_card_downloaded', { reference: ref, doc_id: doc.id, membre_id: membreId });
    setLoadingId(doc.id);
    try { await downloadDocument(doc); }
    finally { setLoadingId(null); }
  };

  const handlePreview = (doc) => {
    const ref = (doc.metadata && doc.metadata.devis_id) || doc.reference || doc.id;
    trackEvent('proposition_card_previewed', { reference: ref, doc_id: doc.id, membre_id: membreId });
    setPreviewDoc({
      id: doc.id,
      original_filename: doc.nom || doc.original_filename || `Proposition_${ref}.pdf`,
      filename: doc.nom || `Proposition_${ref}.pdf`,
      mime_type: 'application/pdf',
      size: doc.file_size || doc.size,
      source: 'PROPOSITION_ESS',
      created_at: doc.date_creation || doc.created_at,
      _previewUrl: resolvePropositionPreviewUrl(doc),
      _originalDoc: doc,
    });
  };

  const previewProposition = async (docId) => {
    if (previewDoc?._previewUrl) {
      return previewDocumentBlob(docId, previewDoc._previewUrl);
    }
    throw new Error("URL d'aperçu introuvable");
  };

  const downloadFromViewer = (doc) => {
    if (doc._originalDoc) handleDownload(doc._originalDoc);
  };

  return (
    <div data-testid="propositions-ess-section">
      <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-purple-600" />
        Propositions ESS BATICO ({items.length})
      </h4>
      <div className="space-y-3">
        {items.map((doc) => {
          const montant = extractMontantTTC(doc);
          const montantStr = formatEUR(montant);
          const { date: validiteDate, joursRestants, expired } = extractValidite(doc);
          const ref = (doc.metadata && doc.metadata.devis_id) || doc.reference || doc.id;
          const dateCreation = doc.date_creation || doc.created_at;
          const isFromSimulator = doc.source === 'ged_simulateur'
            || doc.source === 'ged'
            || doc.type === 'devis_ess_genere';

          const validityColor = expired
            ? 'text-slate-500'
            : (joursRestants !== null && joursRestants <= 7)
              ? 'text-amber-700'
              : 'text-emerald-700';
          const validityBg = expired
            ? 'bg-slate-100'
            : (joursRestants !== null && joursRestants <= 7)
              ? 'bg-amber-50 border-amber-200'
              : 'bg-emerald-50 border-emerald-200';
          const validityIcon = expired
            ? <AlertTriangle className="h-3.5 w-3.5" />
            : (joursRestants !== null && joursRestants <= 7)
              ? <Clock className="h-3.5 w-3.5" />
              : <CheckCircle className="h-3.5 w-3.5" />;

          return (
            <Card
              key={doc.id}
              className={`border border-purple-200 bg-gradient-to-br from-purple-50 via-violet-50 to-fuchsia-50 shadow-sm hover:shadow-md transition-shadow ${isSelected(doc.id) ? 'ring-2 ring-purple-400' : ''}`}
              data-testid={`proposition-card-${doc.id}`}
            >
              <CardContent className="p-0">
                <div className="flex flex-col lg:flex-row">
                  <div className="flex items-start gap-3 p-4 lg:flex-1 border-b lg:border-b-0 lg:border-r border-purple-200/60">
                    {onToggle && (
                      <input
                        type="checkbox"
                        checked={isSelected(doc.id)}
                        onChange={() => onToggle({ type: 'proposition_ess', id: doc.id, label: doc.nom || `Proposition #${ref}` })}
                        className="mt-1.5 h-4 w-4 rounded border-purple-400 text-purple-600 focus:ring-purple-500 cursor-pointer flex-shrink-0"
                        aria-label={`Sélectionner la proposition ${ref}`}
                        data-testid={`select-proposition-${doc.id}`}
                      />
                    )}
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-600 to-violet-600 flex items-center justify-center shadow-md">
                        <FileText className="h-6 w-6 text-white" />
                      </div>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <Badge className="bg-purple-600 text-white text-[10px] uppercase tracking-wider font-bold">
                          Proposition ESS
                        </Badge>
                        {isFromSimulator && (
                          <Badge variant="outline" className="border-purple-400 text-purple-700 text-[10px]">
                            <Sparkles className="h-2.5 w-2.5 mr-1" />
                            Simulateur BATICO
                          </Badge>
                        )}
                        {(doc.metadata && doc.metadata.reprise_simulateur) && (
                          <Badge
                            variant="outline"
                            className="border-amber-400 text-amber-700 bg-amber-50 text-[10px]"
                            data-testid={`proposition-reprise-badge-${doc.id}`}
                          >
                            <RefreshCw className="h-2.5 w-2.5 mr-1" />
                            Reprise simulateur
                          </Badge>
                        )}
                        {doc.email_sent && (
                          <Badge variant="outline" className="border-emerald-400 text-emerald-700 text-[10px]">
                            <Mail className="h-2.5 w-2.5 mr-1" />
                            Email envoyé
                          </Badge>
                        )}
                        {(() => {
                          const versions = versionsByDoc[ref] || [];
                          // V36 — Badge toujours affiché si on a au moins
                          // une version retournée par l'API (régression : on
                          // affichait uniquement quand >=2 versions, ce qui
                          // rendait l'historique invisible la plupart du temps).
                          if (versions.length < 1) return null;
                          const currentVersion = versions.find(v => v.devis_id === ref);
                          const currentLabel = currentVersion ? `v${currentVersion.version}` : 'v?';
                          const isUnique = versions.length === 1;
                          return (
                            <Popover>
                              <PopoverTrigger asChild>
                                <button
                                  type="button"
                                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[10px] font-bold uppercase tracking-wider transition-colors ${
                                    isUnique
                                      ? 'border-slate-300 bg-slate-50 hover:bg-slate-100 text-slate-700'
                                      : 'border-violet-400 bg-violet-50 hover:bg-violet-100 text-violet-800'
                                  }`}
                                  data-testid={`proposition-versions-trigger-${doc.id}`}
                                  title={isUnique ? 'Aucune version antérieure pour cette proposition' : "Voir l'historique des versions de cette proposition"}
                                >
                                  <History className="h-2.5 w-2.5" />
                                  {currentLabel} <span className={isUnique ? 'text-slate-500 font-normal' : 'text-violet-500 font-normal'}>/ {versions.length}</span>
                                  <ChevronDown className="h-2.5 w-2.5" />
                                </button>
                              </PopoverTrigger>
                              <PopoverContent
                                align="start"
                                className="w-[420px] p-0 max-h-[420px] overflow-y-auto"
                                data-testid={`proposition-versions-popover-${doc.id}`}
                              >
                                <div className="px-3 py-2 border-b border-slate-200 bg-violet-50/60">
                                  <div className="text-[11px] font-semibold text-violet-900 uppercase tracking-wider">
                                    Historique des versions
                                  </div>
                                  <div className="text-[10px] text-violet-700 mt-0.5">
                                    Comparez les montants et rouvrez n&apos;importe quelle version dans le simulateur.
                                  </div>
                                </div>
                                <div className="divide-y divide-slate-100">
                                  {versions.map((v) => {
                                    const isCurrent = v.devis_id === ref;
                                    const created = v.created_at ? new Date(v.created_at) : null;
                                    return (
                                      <div
                                        key={v.devis_id}
                                        className={`px-3 py-2.5 text-xs ${isCurrent ? 'bg-violet-50/40' : 'hover:bg-slate-50'} transition-colors`}
                                        data-testid={`proposition-version-row-${v.version}`}
                                      >
                                        <div className="flex items-start justify-between gap-2 mb-1.5">
                                          <div className="flex items-center gap-2 min-w-0">
                                            <Badge className={`text-[10px] font-bold ${isCurrent ? 'bg-violet-600 text-white' : 'bg-slate-200 text-slate-700'}`}>
                                              v{v.version}
                                            </Badge>
                                            {isCurrent && (
                                              <span className="text-[10px] text-violet-700 font-semibold">Affichée</span>
                                            )}
                                            <span className="text-[10px] text-slate-400 font-mono truncate">
                                              #{(v.devis_id || '').slice(-8)}
                                            </span>
                                          </div>
                                          {created && (
                                            <span className="text-[10px] text-slate-500 whitespace-nowrap">
                                              {created.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: '2-digit' })}
                                            </span>
                                          )}
                                        </div>
                                        <div className="flex items-center justify-between gap-2">
                                          <div>
                                            <div className="text-[10px] text-slate-500 uppercase tracking-wider">Total TTC</div>
                                            <div className="text-base font-bold text-violet-900 leading-none">
                                              {v.montant_ttc ? `${Number(v.montant_ttc).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €` : '—'}
                                            </div>
                                            {v.ca_ht ? (
                                              <div className="text-[10px] text-slate-500 mt-0.5">
                                                CA : {Number(v.ca_ht).toLocaleString('fr-FR')} € HT
                                              </div>
                                            ) : null}
                                          </div>
                                          <div className="flex gap-1.5">
                                            <Button
                                              size="sm"
                                              variant="outline"
                                              className="h-7 px-2 text-[11px] border-purple-300 text-purple-700 hover:bg-purple-50"
                                              onClick={() => handleVersionPreview(doc, v)}
                                              data-testid={`proposition-version-preview-${v.version}`}
                                              title="Aperçu de cette version"
                                            >
                                              <Eye className="h-3 w-3" />
                                            </Button>
                                            <Button
                                              size="sm"
                                              variant="outline"
                                              className="h-7 px-2 text-[11px] border-purple-300 text-purple-700 hover:bg-purple-50"
                                              onClick={() => handleVersionDownload(doc, v)}
                                              disabled={loadingId === `${doc.id}::v${v.version}`}
                                              data-testid={`proposition-version-download-${v.version}`}
                                              title="Télécharger le PDF de cette version"
                                            >
                                              {loadingId === `${doc.id}::v${v.version}`
                                                ? <Loader2 className="h-3 w-3 animate-spin" />
                                                : <Download className="h-3 w-3" />}
                                            </Button>
                                            <Button
                                              size="sm"
                                              variant="outline"
                                              className="h-7 px-2 text-[11px] border-amber-300 text-amber-700 hover:bg-amber-50"
                                              onClick={() => handleVersionResume(v)}
                                              data-testid={`proposition-version-resume-${v.version}`}
                                              title="Rouvrir cette version dans le simulateur"
                                            >
                                              <Calculator className="h-3 w-3" />
                                            </Button>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </PopoverContent>
                            </Popover>
                          );
                        })()}
                        {versionsLoading[ref] && (
                          <span className="text-[10px] text-slate-400 inline-flex items-center gap-1">
                            <Loader2 className="h-2.5 w-2.5 animate-spin" /> versions
                          </span>
                        )}
                      </div>
                      <p className="font-semibold text-slate-900 text-sm leading-tight truncate" title={doc.nom}>
                        {doc.nom || 'Proposition ESS BATICO'}
                      </p>
                      {doc.metadata?.avenant_de?.numero_police && (
                        <span className="inline-block mt-1 rounded-full bg-violet-100 text-violet-700 border border-violet-300 px-2 py-0.5 text-[11px] font-medium" data-testid={`proposition-avenant-${doc.id}`}>
                          Avenant au contrat parent {doc.metadata.avenant_de.numero_police}
                        </span>
                      )}
                      <p className="text-xs text-slate-500 font-mono mt-0.5">#{ref}</p>
                      {dateCreation && (
                        <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          Émise le {new Date(dateCreation).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-row lg:flex-col lg:w-56 divide-x lg:divide-x-0 lg:divide-y divide-purple-200/60 bg-white/40">
                    <div className="flex-1 p-4 text-center">
                      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1 leading-tight">
                        Total indicatif<br />du parcours coopératif
                      </div>
                      {(() => {
                        // Logique strictement alignée avec le simulateur
                        // (CalculateurBATICO/ResultatsSection.js ligne ~429) :
                        // Total indicatif = montant_ttc + 200€ (cotisation FTPE annuelle)
                        // si le membre n'est PAS adhérent FTPE, sinon montant_ttc seul.
                        const ttc = montant ? Number(montant) : null;
                        if (ttc == null || Number.isNaN(ttc)) {
                          return (
                            <div>
                              <div className="text-xl lg:text-2xl font-bold text-purple-900 leading-none" data-testid={`proposition-montant-${doc.id}`}>—</div>
                              <div className="mt-1 text-[11px] text-amber-700" data-testid={`proposition-montant-absent-${doc.id}`}>
                                Montant non calculé — relancez le simulateur
                              </div>
                            </div>
                          );
                        }
                        const dejaAdherent = isFtpeMember || dejaAdherentByDoc[ref] === true
                          || (doc.ftpe_masque && doc.ftpe_masque.motif === 'deja_adherent');
                        const total = dejaAdherent ? ttc : ttc + 200;
                        const totalStr = total.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                        return (
                          <>
                            <div className="text-xl lg:text-2xl font-bold text-purple-900 leading-none" data-testid={`proposition-montant-${doc.id}`}>
                              {totalStr} €
                            </div>
                            {dejaAdherent ? (
                              <div className="mt-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-[10px] font-semibold text-emerald-700" data-testid={`proposition-deja-adherent-${doc.id}`}>
                                <CheckCircle className="h-3 w-3" /> Déjà adhérent
                              </div>
                            ) : (
                              <div className="text-[9px] text-slate-500 mt-1 leading-tight">
                                {montantStr} € + 200 € FTPE
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                    <div className="flex-1 p-4 text-center">
                      <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md border text-xs font-semibold ${validityBg} ${validityColor}`}>
                        {validityIcon}
                        {expired
                          ? 'Expirée'
                          : joursRestants !== null
                            ? `Valable ${joursRestants}j`
                            : 'Valable'}
                      </div>
                      {validiteDate && (
                        <div className="text-[10px] text-slate-500 mt-1">
                          Jusqu&apos;au {validiteDate.toLocaleDateString('fr-FR')}
                        </div>
                      )}
                      <EcheancierCarte ech={doc.echelonnement} docId={doc.id} />
                      <MandatSepaLigne mandat={doc.mandat_sepa} docId={doc.id} />
                    </div>
                  </div>

                  <div className="flex flex-row lg:flex-col gap-2 p-4 lg:w-52 bg-white/60">
                    <Button variant="outline" size="sm" className="border-purple-300 text-purple-700 hover:bg-purple-100 flex-1 lg:flex-none" onClick={() => handlePreview(doc)} data-testid={`proposition-view-${doc.id}`}>
                      <Eye className="h-4 w-4 mr-1" /> Aperçu
                    </Button>
                    <Button variant="outline" size="sm" className="border-purple-300 text-purple-700 hover:bg-purple-100 flex-1 lg:flex-none" onClick={() => handleDownload(doc)} disabled={loadingId === doc.id} data-testid={`proposition-download-${doc.id}`}>
                      {loadingId === doc.id ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />}
                      PDF
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-amber-300 text-amber-800 hover:bg-amber-50 flex-1 lg:flex-none"
                      onClick={() => handleReprendreSimulateur(doc)}
                      data-testid={`proposition-reprendre-simulateur-${doc.id}`}
                      title="Rouvrir cette proposition dans le simulateur RCDS BATICO"
                    >
                      <Calculator className="h-4 w-4 mr-1" />
                      Simulateur
                    </Button>
                    <PropositionSignatureAction devisId={ref} docId={doc.id} expired={expired} />
                    {!expired && (montant ? (
                      <Button size="sm" className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-semibold flex-1 lg:flex-none" onClick={() => handleSouscrire(doc)} data-testid={`proposition-souscrire-${doc.id}`}>
                        Souscrire <ArrowRight className="h-4 w-4 ml-1" />
                      </Button>
                      ) : (
                      <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-white font-semibold flex-1 lg:flex-none" onClick={() => handleRelancer(doc)} data-testid={`proposition-relancer-${doc.id}`}>
                        Relancer le simulateur <ArrowRight className="h-4 w-4 ml-1" />
                      </Button>
                    ))}
                    {onDeleteOne && (
                      <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 hover:bg-red-50 flex-1 lg:flex-none" onClick={() => onDeleteOne({ type: 'proposition_ess', id: doc.id, label: doc.nom || `Proposition #${ref}` })} title="Supprimer cette proposition" data-testid={`delete-proposition-${doc.id}`}>
                        <Trash2 className="h-4 w-4 mr-1" /> Supprimer
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
      {previewDoc && (
        <GEDDocumentViewerModal
          doc={previewDoc}
          onClose={() => setPreviewDoc(null)}
          onDownload={downloadFromViewer}
          previewDocument={previewProposition}
        />
      )}
    </div>
  );
}
